import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Loan, LoanInput } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { centsToEuros, eurosToCents } from '@/lib/money';
import { percentToRatio, ratioToPercent } from '@/lib/rate';

const loanFormSchema = z.object({
  lender: z.string().trim().min(1, 'Requis').max(120),
  principal: z.number().positive('Requis'),
  annualRate: z.number().min(0).max(25),
  insuranceAnnualRate: z.number().min(0).max(25),
  durationMonths: z.number().int().min(1).max(480),
  startDate: z.iso.date(),
});

type LoanFormValues = z.infer<typeof loanFormSchema>;

function toFormValues(loan?: Loan): LoanFormValues {
  return {
    lender: loan?.lender ?? '',
    principal: loan ? centsToEuros(loan.principalCents) : 0,
    annualRate: loan ? ratioToPercent(loan.annualRate) : 0,
    insuranceAnnualRate: loan ? ratioToPercent(loan.insuranceAnnualRate) : 0,
    durationMonths: loan?.durationMonths ?? 240,
    startDate: loan?.startDate ?? todayLocalIsoDate(),
  };
}

/** Date du jour en `AAAA-MM-JJ`, dans le fuseau du navigateur — `toISOString()` donnerait la date UTC. */
function todayLocalIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toInput(values: LoanFormValues): LoanInput {
  return {
    lender: values.lender,
    principalCents: eurosToCents(values.principal),
    annualRate: percentToRatio(values.annualRate),
    insuranceAnnualRate: percentToRatio(values.insuranceAnnualRate),
    durationMonths: values.durationMonths,
    startDate: values.startDate,
  };
}

export function LoanForm({
  loan,
  locked,
  onSubmit,
  isPending,
}: {
  loan?: Loan;
  /** Conditions verrouillées : l'échéancier existe déjà (voir `loan_terms_locked`). */
  locked?: boolean;
  onSubmit: (input: LoanInput) => void;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoanFormValues>({
    resolver: zodResolver(loanFormSchema),
    defaultValues: toFormValues(loan),
  });

  return (
    <form onSubmit={handleSubmit((values) => onSubmit(toInput(values)))} className="flex flex-col gap-4">
      {locked && (
        <Alert>
          <AlertDescription>
            L'échéancier de ce prêt existe déjà : le capital, les taux, la durée et la date de déblocage ne
            peuvent plus changer. Archivez ce prêt et créez-en un nouveau si besoin — le nom de la banque, lui,
            reste modifiable.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lender">Banque</Label>
        <Input id="lender" {...register('lender')} />
        {errors.lender && <p className="text-sm text-destructive">{errors.lender.message}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="principal">Capital emprunté (€)</Label>
          <Input id="principal" type="number" step="0.01" min="0" disabled={locked} {...register('principal', { valueAsNumber: true })} />
          {errors.principal && <p className="text-sm text-destructive">{errors.principal.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="durationMonths">Durée (mois)</Label>
          <Input
            id="durationMonths"
            type="number"
            step="1"
            min="1"
            disabled={locked}
            {...register('durationMonths', { valueAsNumber: true })}
          />
          {errors.durationMonths && <p className="text-sm text-destructive">{errors.durationMonths.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="annualRate">Taux nominal annuel (%)</Label>
          <Input
            id="annualRate"
            type="number"
            step="0.01"
            min="0"
            max="25"
            disabled={locked}
            {...register('annualRate', { valueAsNumber: true })}
          />
          {errors.annualRate && <p className="text-sm text-destructive">{errors.annualRate.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="insuranceAnnualRate">Taux assurance annuel (%)</Label>
          <Input
            id="insuranceAnnualRate"
            type="number"
            step="0.01"
            min="0"
            max="25"
            disabled={locked}
            {...register('insuranceAnnualRate', { valueAsNumber: true })}
          />
          {errors.insuranceAnnualRate && (
            <p className="text-sm text-destructive">{errors.insuranceAnnualRate.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="startDate">Date de déblocage</Label>
          <Input id="startDate" type="date" disabled={locked} {...register('startDate')} />
          {errors.startDate && <p className="text-sm text-destructive">{errors.startDate.message}</p>}
        </div>
      </div>

      <Button type="submit" disabled={isPending} className="self-start">
        {isPending ? 'Enregistrement…' : loan ? 'Enregistrer' : 'Créer le prêt'}
      </Button>
    </form>
  );
}
